// @flow
import Immutable, { Map } from "immutable"
import * as R from "ramda"
import { createSelector } from "reselect"
import type { Dispatch } from "redux"

import type { GetState } from "common/types/state"
import { bidSelector, bookingDataSelector } from "../../../services/selector"
import { submitForPayment } from "../../BookingPayment/services/paymentActions"
import { bookedAtSelector } from "common/services/branding/selectors"
import { currencySelector } from "common/services/currencies/selectors"
import { mapperCreatedBookingChange } from "../../../services/mapper/services/mapperManageBookingChange"
import { SERVICES, SERVICES_STATUSES } from "mmb/consts/additionalServices"
import { SUBMIT_MEALS_ORDER } from "../constants"
import type { ApiSelection, MealsPostDataType, PaymentData } from "./api"
import { fetchMealsOptions, fetchOrderMeals } from "./api"
import { mapperMealOptions } from "./helpers/mapperMealOptions"
import type {
  ItineraryMealOptions,
  Meal,
  MealFormSelection,
  MealItem,
  MealItems,
  Meals,
  MealSelection,
  MealSelections,
  OrderedMeals,
  SelectedMeals,
} from "./mealTypes"
import type { ServiceForPaymentType } from "../../../services/types"
import type { MealSegmentsInput } from "mmb/records/mealsInput"
import convertPrice from "common/services/price/convertPrice"
import { getCurrencies } from "common/services/currencies"
import {
  DESELECT_MEAL,
  LOAD_MEALS,
  LOAD_MEALS_ERROR,
  LOAD_MEALS_SUCCESS,
  REINITIALIZE,
  SELECT_MEAL,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingMeals/services/mealConsts"

type typeReducer = Map<{
  loading: boolean,
  error: boolean,
  mealOptions: {} | ItineraryMealOptions,
}>

// REDUCER’s INITIAL STATE
const INITIAL_STATE: typeReducer = Map({
  loading: true,
  error: false,
  orderSuccess: false,
  mealsOptions: {},
  selectedMeals: {},
})

type actionWithPayload = any
type actionWithoutPayload = any

// REDUCER
export default function bookingMealsReducer(
  state: typeReducer = INITIAL_STATE,
  action: actionWithPayload | actionWithoutPayload,
) {
  switch (action.type) {
    case LOAD_MEALS:
      return state.set("loading", true).set("error", false)
    case LOAD_MEALS_SUCCESS:
      return state
        .set("mealsOptions", action.res || action.payload.mealOptions)
        .set("loading", false)
    case LOAD_MEALS_ERROR:
      return state.set("loading", false).set("error", true)
    case SELECT_MEAL:
      return state.mergeDeep({
        selectedMeals: {
          [action.payload.segmentId]: { [action.payload.passengerId]: action.payload.mealChoice },
        },
      })
    case DESELECT_MEAL:
      return state.deleteIn([
        "selectedMeals",
        action.payload.segmentId,
        String(action.payload.passengerId),
      ])
    case REINITIALIZE:
      return state.set("selectedMeals", Map())
    default:
      return state
  }
}

// Selectors
export const mealsOptionsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
): ItineraryMealOptions | {} => {
  const options = state.bookingMeals.get("mealsOptions")

  return options instanceof Immutable.Map ? options.toJS() : options
}

export const areMealsLoadingSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
): boolean => state.bookingMeals.get("loading")

export const isMealsLoadingErrorSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
): boolean => state.bookingMeals.get("error")

// $FlowFixMe
export const bookingMealsSelector = createSelector(
  bookingDataSelector,
  booking => booking.meals,
)
/*
 * gather ordered meals and map it to segmentId: order_shape
 */

const additionalServicesListSelector = createSelector(
  bookingDataSelector,
  data => data.additionalServices.reduce((acc, curr) => ({ ...acc, [curr.get("id")]: curr }), {}),
)

// $FlowFixMe
export const orderedMealsSelector = createSelector(
  [bookingMealsSelector, bookingDataSelector, additionalServicesListSelector],
  /*
   *
   * @param meals
   * @param booking
   * @returns {{}}
   */
  (meals: Meals, booking, additionalServices): OrderedMeals => {
    if (!meals) {
      return {}
    }
    const confirmedMealsAdditionalServices = booking.additionalServices
      .filter(
        change =>
          change.category === SERVICES.MEALS &&
          [
            SERVICES_STATUSES.CONFIRMED,
            SERVICES_STATUSES.CLOSED,
            SERVICES_STATUSES.PENDING,
          ].includes(change.status),
      )
      .map(change => change.id)

    const confirmedMealsItems: Array<MealItems> = meals
      .filter((meal: Meal) => confirmedMealsAdditionalServices.includes(meal.abid))
      .map((orderedMeal: Meal) =>
        orderedMeal.items.map(item => ({
          ...item,
          orderStatus: additionalServices[orderedMeal.abid].get("status"),
        })),
      )
    // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
    const flat = R.flatten(confirmedMealsItems)
    // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
    const groupedByFlight = R.groupBy((meal: MealItem) => meal.segmentId, flat)
    const mapped = Object.entries(groupedByFlight).reduce((acc, [segmentId, orderedItems]) => {
      // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
      acc[segmentId] = R.groupBy((item: MealItem) => item.paxId, orderedItems)
      return acc
    }, {})
    const mappedOrderedMeal = Object.entries(mapped).reduce(
      (acc, [segmentId, paxOrder]) => {
        // $FlowFixMe
        const stuff = Object.entries(paxOrder).reduce((suck, [paxId, orderedItems]) => {
          // $FlowFixMe
          acc[segmentId][paxId] = orderedItems[0]
          return acc
        }, {})

        return R.mergeDeepRight(mappedOrderedMeal, stuff)
      },

      mapped,
    )
    return mappedOrderedMeal
  },
)

export const selectedMealsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
): SelectedMeals => {
  return state.bookingMeals.get("selectedMeals").toJS()
}

export const saveMealOptionsToStateAction = (res: MealSegmentsInput) => {
  const mealOptions: ItineraryMealOptions = mapperMealOptions(res.segments)

  return {
    type: LOAD_MEALS_SUCCESS,
    payload: {
      mealOptions,
    },
  }
}

export const showMealsError = () => ({
  type: LOAD_MEALS_ERROR,
})

export const selectMealAction = (selection: MealFormSelection) => ({
  type: SELECT_MEAL,
  payload: {
    ...selection,
  },
})

export const deselectMealAction = (selection: MealFormSelection) => ({
  type: DESELECT_MEAL,
  payload: {
    ...selection,
  },
})

export const reinitializeAction = () => ({
  type: REINITIALIZE,
})

// TODO move this logic to loadMealsAction and inctoduce, shouldRetry flag there
export const loadMealsFromModalAction = () => (dispatch: Dispatch<any>, getState: GetState) => {
  const state = getState()
  const bid = bidSelector(state)
  function callRequestDetail(retryCount, timeout) {
    fetchMealsOptions(bid).then((res: MealSegmentsInput) => {
      if (res && res !== null && res.segments) {
        dispatch(saveMealOptionsToStateAction(res))
      } else if (retryCount > 30) {
        dispatch(showMealsError())
      } else {
        setTimeout(() => {
          callRequestDetail(retryCount + 1, timeout + 1000)
        }, timeout)
      }
    })
  }
  callRequestDetail(0, 4000)
}

// TODO move to helpers
const mapSelectedMealsForApi = (frontendOrder: MealSelections): Array<ApiSelection> =>
  frontendOrder.map(({ meal, segmentId, paxId }: MealSelection) => ({
    option_code: meal,
    passenger_id: Number(paxId),
    segment_code: segmentId,
  }))

// eslint-disable-next-line flowtype/no-weak-types
export const orderMealsAction = (serviceForPayment: ServiceForPaymentType) => async (
  dispatch: Dispatch<any>,
  getState: Function, // eslint-disable-line flowtype/no-weak-types
) => {
  try {
    const state = getState()
    const { payload } = serviceForPayment
    const { order, currency } = payload
    const booking = bookingDataSelector(state)
    const bid = booking.id

    const price = String(serviceForPayment.price)
    const upperCaseCurrency = currency.toUpperCase()

    // TODO create helper function for mapping data to BE payload and test that currency is uppercase
    const paymentData: PaymentData = {
      paid_by_card: {
        gate: "zooz",
        price: {
          amount: !serviceForPayment.payload.useBalance ? price : "0",
          currency: upperCaseCurrency,
        },
      },
      paid_by_credits: {
        price: {
          amount: serviceForPayment.payload.useBalance ? price : "0",
          currency: upperCaseCurrency,
        },
      },
    }

    const mealsPayload: MealsPostDataType = {
      booked_at: bookedAtSelector(state),
      orig_price: String(payload.price), // price in eur
      payment: paymentData,
      selections: mapSelectedMealsForApi(order),
    }

    const promise = fetchOrderMeals(bid, mealsPayload)

    const ok = await dispatch({
      type: SUBMIT_MEALS_ORDER,
      action: SUBMIT_MEALS_ORDER,
      payload,
      promise,
      _track: { payload },
    })

    if (ok) {
      return promise.then(mapperCreatedBookingChange).catch(err => err)
    }
    return ok
  } catch (e) {
    return e
  }
}

export const toPaymentAction = (order: MealSelections, price: number) => (
  dispatch: Dispatch<any>,
  // eslint-disable-next-line flowtype/no-weak-types
  getState: Function,
) => {
  const state = getState()
  const bid = bidSelector(state)
  const currency = currencySelector(state)
  const currencies = getCurrencies(state)
  const convertedPrice = convertPrice(price, currency, currencies)

  return dispatch(
    submitForPayment({
      category: SERVICES.MEALS,
      bid,
      price: convertedPrice,
      currency,
      orderAction: orderMealsAction,
      useBalance: false,
      payload: {
        price,
        order,
        currency,
      },
    }),
  )
}
